/** Faceted filter panel — category checkboxes and in-stock toggle. */

import { h, Fragment, render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { FilterState } from '../hooks/useFilters';
import type { FacetBucket } from '../api';

/** "ISO 5"-style buckets the gateway serves inside certifications_list.
 *  Anything else (kill-switch reverting the facet to real certifications)
 *  falls back to the plain checkbox row. */
const ISO_BUCKET_RE = /^ISO\s*(\d+(?:\.\d+)?)$/i;

/** Customer-facing one-liner for the ISO Grade info tip, in the published
 *  vocabulary of Terra's Cleanroom FAQ ("The cleanest classification,
 *  ISO 3, denotes … The least rigorous classification, ISO 8, specifies …"
 *  — blog/frequently-asked-questions-before-purchasing-clean-rooms),
 *  scoped to the catalog's certified ISO 4–8 scale. */
const ISO_GRADE_TIP = 'ISO 4 is the cleanest classification; ISO 8 is the least rigorous.';

/* Keep in sync with .ts-filter-info-pop max-width in widget.css. */
const TIP_MAX_WIDTH = 236;
/* Worst-case rendered tip height (three wrapped lines + padding) — only used
 * to decide whether to flip above the button near the viewport bottom. */
const TIP_EST_HEIGHT = 84;

/** The popover renders into a top-level layer of the widget's shadow root.
 *  Anchored absolute inside the panel it gets clipped by the desktop
 *  sidebar's scroll container (overflow-y: auto) and trapped under the
 *  results column by the sticky sidebar's stacking context — the portal +
 *  fixed coordinates escape both, and styles still apply because the layer
 *  stays inside the same shadow root. */
function getTipLayer(from: HTMLElement): HTMLElement {
  const root = from.getRootNode();
  const host: ShadowRoot | HTMLElement = root instanceof ShadowRoot ? root : document.body;
  let layer = host.querySelector('.ts-tip-layer') as HTMLElement | null;
  if (!layer) {
    layer = document.createElement('div');
    layer.className = 'ts-tip-layer';
    host.appendChild(layer);
  }
  return layer;
}

interface TipPos {
  top: number;
  left: number;
  arrow: number;
  up: boolean;
}

/** Small ⓘ next to the ISO Grade title. Hover opens it with a mouse; tap
 *  toggles it on touch (the synthetic mouseenter/mouseleave that browsers
 *  fire around a tap are ignored via the touch flag). Outside tap, Escape,
 *  scroll, or pointer-leave closes. Stays up while reading (unlike
 *  QuickTooltip's 1.5s auto-hide). */
function IsoGradeInfoTip() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TipPos | null>(null);
  const touchRef = useRef(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    const anchor = wrapRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const width = Math.min(TIP_MAX_WIDTH, window.innerWidth * 0.72);
    const left = Math.min(Math.max(8, r.left - 8), window.innerWidth - width - 8);
    const up = r.bottom + 7 + TIP_EST_HEIGHT > window.innerHeight && r.top - 7 - TIP_EST_HEIGHT > 0;
    setPos({
      top: up ? r.top - 7 : r.bottom + 7,
      left,
      arrow: Math.max(8, Math.min(r.left + r.width / 2 - left - 4, width - 16)),
      up,
    });
    setOpen(true);
  };

  // Mini-portal: the popover is rendered into the shadow root's top-level
  // tip layer with preact's own render() — createPortal lives in
  // preact/compat, which costs ~8KB of bundle for this one element.
  useEffect(() => {
    if (!open || !pos) return;
    const anchor = wrapRef.current;
    if (!anchor) return;
    const layer = getTipLayer(anchor);
    render(
      <span
        id="ts-iso-grade-tip"
        ref={(el: HTMLSpanElement | null) => {
          popRef.current = el;
        }}
        class={`ts-filter-info-pop ${pos.up ? 'ts-filter-info-pop--up' : ''}`}
        role="tooltip"
        style={{ top: `${pos.top}px`, left: `${pos.left}px`, '--ts-tip-arrow': `${pos.arrow}px` }}
      >
        {ISO_GRADE_TIP}
      </span>,
      layer,
    );
    return () => {
      popRef.current = null;
      render(null, layer);
    };
  }, [open, pos]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: Event) => {
      const path = e.composedPath();
      const insideWrap = wrapRef.current && path.includes(wrapRef.current);
      const insidePop = popRef.current && path.includes(popRef.current);
      if (!insideWrap && !insidePop) setOpen(false);
    };
    // Fixed coordinates go stale the moment the sidebar, drawer body, or
    // page scrolls — close instead of chasing the anchor.
    const onMove = () => setOpen(false);
    document.addEventListener('click', onDocClick, true);
    document.addEventListener('scroll', onMove, { capture: true, passive: true });
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  return (
    <span class="ts-filter-info" ref={wrapRef}>
      <button
        type="button"
        class="ts-filter-info-btn"
        aria-label="What does ISO Grade mean?"
        aria-expanded={open}
        aria-describedby={open ? 'ts-iso-grade-tip' : undefined}
        onTouchStart={() => {
          touchRef.current = true;
        }}
        onMouseEnter={() => {
          if (!touchRef.current) show();
        }}
        onMouseLeave={() => {
          if (!touchRef.current) setOpen(false);
        }}
        onClick={() => {
          if (touchRef.current) {
            if (open) setOpen(false);
            else show();
          } else {
            show();
          }
        }}
        onBlur={() => {
          if (!touchRef.current) setOpen(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="11" x2="12" y2="16.5" />
          <circle cx="12" cy="7.5" r="0.5" fill="currentColor" stroke="none" />
        </svg>
      </button>
    </span>
  );
}

interface FilterPanelProps {
  facets: {
    categories: FacetBucket[];
    certifications: FacetBucket[];
    manufacturers: FacetBucket[];
  };
  filters: FilterState;
  onToggleCategory: (category: string) => void;
  onToggleCertification: (cert: string) => void;
  onToggleManufacturer: (manufacturer: string) => void;
  onFilterChange: (updates: Partial<FilterState>) => void;
}

// Categories to hide from the filter panel
const HIDDEN_CATEGORIES = new Set([
  'Zee Other Stuff',
  'Not Categorized',
  'Misc',
  'Freight',
  'Service',
  'CR Acsrs Only',
]);

export function FilterPanel({
  facets,
  filters,
  onToggleCategory,
  onToggleCertification,
  onToggleManufacturer,
  onFilterChange,
}: FilterPanelProps) {
  const [categorySearch, setCategorySearch] = useState('');
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const [certificationsExpanded, setCertificationsExpanded] = useState(false);
  const [manufacturersExpanded, setManufacturersExpanded] = useState(false);

  const visibleCategories = facets.categories.filter((c) => !HIDDEN_CATEGORIES.has(c.value));
  const filteredCategories = categorySearch
    ? visibleCategories.filter((c) => c.value.toLowerCase().includes(categorySearch.toLowerCase()))
    : visibleCategories;
  const visibleCategoryRows = categoriesExpanded || categorySearch
    ? filteredCategories
    : filteredCategories.slice(0, 8);
  const visibleCertificationRows = certificationsExpanded
    ? facets.certifications
    : facets.certifications.slice(0, 6);
  const visibleManufacturerRows = manufacturersExpanded
    ? facets.manufacturers
    : facets.manufacturers.slice(0, 8);

  return (
    <div class="ts-filters">
      {/* In Stock */}
      <div class="ts-filter-section">
        <h4 class="ts-filter-section-title">Availability</h4>
        <label class="ts-filter-checkbox ts-filter-checkbox--switch">
          <input
            type="checkbox"
            checked={filters.inStockOnly}
            onChange={(e) => onFilterChange({ inStockOnly: (e.target as HTMLInputElement).checked })}
          />
          <span class="ts-filter-switch-track" aria-hidden="true" />
          <span>In Stock Only</span>
        </label>
      </div>

      {/* Price Range */}
      <div class="ts-filter-section">
        <h4 class="ts-filter-section-title">Price Range</h4>
        <div class="ts-filter-price-inputs">
          <label class="ts-filter-price-field">
            <span class="ts-filter-price-label">Min</span>
            <input
              type="number"
              min="0"
              inputMode="numeric"
              placeholder="$0"
              value={filters.priceMin}
              onInput={(e) => onFilterChange({ priceMin: (e.target as HTMLInputElement).value })}
              class="ts-filter-price-input"
              aria-label="Minimum price"
            />
          </label>
          <span class="ts-filter-price-sep" aria-hidden="true">-</span>
          <label class="ts-filter-price-field">
            <span class="ts-filter-price-label">Max</span>
            <input
              type="number"
              min="0"
              inputMode="numeric"
              placeholder="Any"
              value={filters.priceMax}
              onInput={(e) => onFilterChange({ priceMax: (e.target as HTMLInputElement).value })}
              class="ts-filter-price-input"
              aria-label="Maximum price"
            />
          </label>
        </div>
      </div>

      {/* Categories */}
      {visibleCategories.length > 0 && (
        <div class="ts-filter-section">
          <h4 class="ts-filter-section-title">
            Categories ({visibleCategories.length})
          </h4>
          {visibleCategories.length > 8 && (
            <input
              type="text"
              placeholder="Filter categories..."
              value={categorySearch}
              onInput={(e) => setCategorySearch((e.target as HTMLInputElement).value)}
              class="ts-filter-category-search"
            />
          )}
          <div class="ts-filter-category-list">
            {visibleCategoryRows.map((item) => (
              <label key={item.value} class="ts-filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.categories.has(item.value)}
                  onChange={() => onToggleCategory(item.value)}
                />
                <span class="ts-filter-checkbox-label">{item.value}</span>
                <span class="ts-filter-checkbox-count">{item.count}</span>
              </label>
            ))}
          </div>
          {filteredCategories.length > visibleCategoryRows.length && (
            <button type="button" class="ts-filter-show-more" onClick={() => setCategoriesExpanded(true)}>
              Show {filteredCategories.length - visibleCategoryRows.length} more
            </button>
          )}
          {categoriesExpanded && filteredCategories.length > 8 && !categorySearch && (
            <button type="button" class="ts-filter-show-more" onClick={() => setCategoriesExpanded(false)}>
              Show fewer
            </button>
          )}
        </div>
      )}

      {/* Certifications — retitled "ISO Grade" while the gateway serves ISO
          buckets in this facet; reverts to "Certifications" if non-ISO
          values ever come back (gateway kill-switch). */}
      {facets.certifications.length > 0 && (
        <div class="ts-filter-section">
          <h4
            class={`ts-filter-section-title ${
              facets.certifications.some((i) => ISO_BUCKET_RE.test(i.value))
                ? 'ts-filter-section-title--iso'
                : ''
            }`}
          >
            {facets.certifications.some((i) => ISO_BUCKET_RE.test(i.value)) ? (
              <Fragment>
                ISO Grade
                <IsoGradeInfoTip />
              </Fragment>
            ) : (
              `Certifications (${facets.certifications.length})`
            )}
          </h4>
          <div class="ts-filter-category-list">
            {(() => {
              const isoRows = visibleCertificationRows.filter((i) => ISO_BUCKET_RE.test(i.value));
              const otherRows = visibleCertificationRows.filter((i) => !ISO_BUCKET_RE.test(i.value));
              return (
                <Fragment>
                  {isoRows.map((item) => (
                    <label key={item.value} class="ts-filter-checkbox ts-filter-checkbox--iso">
                      <input
                        type="checkbox"
                        checked={filters.certifications.has(item.value)}
                        onChange={() => onToggleCertification(item.value)}
                      />
                      <span class="ts-filter-checkbox-label">{item.value}</span>
                      <span class="ts-filter-checkbox-count">{item.count}</span>
                    </label>
                  ))}
                  {otherRows.map((item) => (
                    <label key={item.value} class="ts-filter-checkbox">
                      <input
                        type="checkbox"
                        checked={filters.certifications.has(item.value)}
                        onChange={() => onToggleCertification(item.value)}
                      />
                      <span class="ts-filter-checkbox-label">{item.value}</span>
                      <span class="ts-filter-checkbox-count">{item.count}</span>
                    </label>
                  ))}
                </Fragment>
              );
            })()}
          </div>
          {facets.certifications.length > visibleCertificationRows.length && (
            <button type="button" class="ts-filter-show-more" onClick={() => setCertificationsExpanded(true)}>
              Show {facets.certifications.length - visibleCertificationRows.length} more
            </button>
          )}
          {certificationsExpanded && facets.certifications.length > 6 && (
            <button type="button" class="ts-filter-show-more" onClick={() => setCertificationsExpanded(false)}>
              Show fewer
            </button>
          )}
        </div>
      )}

      {/* Manufacturers */}
      {facets.manufacturers.length > 0 && (
        <div class="ts-filter-section">
          <h4 class="ts-filter-section-title">
            Manufacturer ({facets.manufacturers.length})
          </h4>
          <div class="ts-filter-category-list">
            {visibleManufacturerRows.map((item) => (
              <label key={item.value} class="ts-filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.manufacturers.has(item.value)}
                  onChange={() => onToggleManufacturer(item.value)}
                />
                <span class="ts-filter-checkbox-label">{item.value}</span>
                <span class="ts-filter-checkbox-count">{item.count}</span>
              </label>
            ))}
          </div>
          {facets.manufacturers.length > visibleManufacturerRows.length && (
            <button type="button" class="ts-filter-show-more" onClick={() => setManufacturersExpanded(true)}>
              Show {facets.manufacturers.length - visibleManufacturerRows.length} more
            </button>
          )}
          {manufacturersExpanded && facets.manufacturers.length > 8 && (
            <button type="button" class="ts-filter-show-more" onClick={() => setManufacturersExpanded(false)}>
              Show fewer
            </button>
          )}
        </div>
      )}
    </div>
  );
}
